import * as React from "react";
import { useSearchParams } from "react-router-dom";

import {
  getMessagePathNodeFromRecord,
  MessagePathRecord,
  TopicRecord,
} from "@/shared/domain/topics";
import { SearchParams } from "@/shared/environment";
import { ErrorMonitoringService } from "@/shared/services";
import { useSubscribeToAiChatPubSubBus } from "@/shared/services/AiChatPubSubBus";
import {
  WORKSPACE_ACTION_TOPIC,
  WorkspaceActionPayload,
} from "@/shared/services/AiChatPubSubBus/models";
import { useVizDispatch } from "@/shared/state/visualization";
import { determineDefaultPanelType } from "@/shared/state/visualization/schema/v1";
import { nanoSecToSecStr } from "@/shared/time";

import { constructPanelForMessagePath } from "../../DndContainer/dropHandlers";
import {
  SidebarTab,
  SidebarViewType,
  useWorkspaceSidebar,
} from "../../sidebarContext";
import { useWorkspaceTimer } from "../../WorkspaceCtx";

/**
 * Hook that subscribes to workspace actions published via PubSubBus
 * and executes them using workspace-specific hooks and context.
 *
 * This allows components outside the workspace (like AI chat sidebar)
 * to trigger workspace actions without direct dependencies.
 */
export function useWorkspaceActionSubscriber() {
  const [_, setSearchParams] = useSearchParams();
  const vizDispatch = useVizDispatch();
  const sidebar = useWorkspaceSidebar();
  const timer = useWorkspaceTimer();

  const handleOpenMessagePath = React.useCallback(
    (
      messagePath: MessagePathRecord,
      topic: TopicRecord,
      timestamp?: bigint,
    ) => {
      // Update search params with timestamp if provided
      setSearchParams(
        (oldParams) => {
          const params = new URLSearchParams(oldParams);
          if (timestamp) {
            params.set(SearchParams.TIMESTAMP, nanoSecToSecStr(timestamp));
          }
          return params;
        },
        { replace: true },
      );

      // Construct the message path node from the records
      const [messagePathNode] = getMessagePathNodeFromRecord(
        topic,
        messagePath,
      );
      if (!messagePathNode) {
        throw new Error(
          `Failed to find message path node for ${messagePath.message_path} when constructing tree from topic ${topic.topic_name} to visualize.`,
        );
      }

      // Create a panel for the message path
      constructPanelForMessagePath(
        vizDispatch,
        messagePathNode,
        determineDefaultPanelType(messagePathNode.data.canonical_data_type),
        (errMsg) => {
          ErrorMonitoringService.captureError(new Error(errMsg));
        },
      );
    },
    [setSearchParams, vizDispatch],
  );

  const handleOpenTopicSearch = React.useCallback(
    (topic: TopicRecord, timestamp?: bigint) => {
      // Update timestamp in URL if provided
      if (timestamp) {
        setSearchParams(
          (oldParams) => {
            const params = new URLSearchParams(oldParams);
            params.set(SearchParams.TIMESTAMP, nanoSecToSecStr(timestamp));
            return params;
          },
          { replace: true },
        );
      }

      // Open the sidebar and switch to Topics tab with initialTopicFilter
      sidebar.openView({
        type: SidebarViewType.TabbedSidebar,
        data: {
          initialTopicFilter: {
            term: topic.topic_name,
            key: Date.now(), // Unique key for command semantics
          },
        },
      });
      sidebar.setActiveTab(SidebarTab.Topics);
    },
    [setSearchParams, sidebar],
  );

  const handleSeekTimer = React.useCallback(
    (timestamp: bigint) => {
      // Update search params with timestamp first
      setSearchParams(
        (oldParams) => {
          const params = new URLSearchParams(oldParams);
          params.set(SearchParams.TIMESTAMP, nanoSecToSecStr(timestamp));
          return params;
        },
        { replace: true },
      );

      // Then seek the timer
      timer.seekTo(timestamp);
    },
    [timer, setSearchParams],
  );

  // Subscribe to workspace actions
  const handleWorkspaceAction = React.useCallback(
    (action: WorkspaceActionPayload) => {
      try {
        switch (action.type) {
          case "open_message_path":
            handleOpenMessagePath(
              action.payload.messagePath,
              action.payload.topic,
              action.payload.timestamp,
            );
            break;
          case "open_topic_search":
            handleOpenTopicSearch(
              action.payload.topic,
              action.payload.timestamp,
            );
            break;
          case "seek_timer":
            handleSeekTimer(action.payload.timestamp);
            break;
          default:
            // Exhaustive check - this should never happen
            ErrorMonitoringService.captureError(
              new Error(
                `Unknown workspace action type: ${JSON.stringify(action)}`,
              ),
            );
        }
      } catch (error) {
        ErrorMonitoringService.captureError(error);
      }
    },
    [handleOpenMessagePath, handleOpenTopicSearch, handleSeekTimer],
  );

  useSubscribeToAiChatPubSubBus<WorkspaceActionPayload>(
    WORKSPACE_ACTION_TOPIC,
    handleWorkspaceAction,
  );
}
